import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { showNotification } from '../../_shared/views/Notifications';
import { LONG_POLLING_INTERVAL } from '../../constants';
import { useSseWithPollingFallback } from '../../utils/sse-or-polling';
import { JobExecution, JobListItem, JobsApi } from './jobs-api';
import { EditJobModel } from './models/job-edit';

const api = new JobsApi();

export function useJobsSSE() {
  const queryClient = useQueryClient();

  const { data: jobs = [], isLoading } = useSseWithPollingFallback<JobListItem[]>({
    queryKey: ['jobs'],
    subscribeSSE: () => api.listSSE((data) => queryClient.setQueryData(['jobs'], data)),
    fetchFallbackFn: () => api.list(),
    refetchInterval: LONG_POLLING_INTERVAL,
  });

  return { jobs, isLoading };
}

export function useCurrentExecutionsSSE() {
  const queryClient = useQueryClient();

  const { data: executions = [], isLoading } = useSseWithPollingFallback<JobExecution[]>({
    queryKey: ['current-executions'],
    subscribeSSE: () => api.currentExecutionsSSE((data) => queryClient.setQueryData(['current-executions'], data)),
    fetchFallbackFn: () => api.currentExecutions(),
    refetchInterval: LONG_POLLING_INTERVAL,
  });

  return { executions, isLoading };
}

export function useJobExecutionsSSE(jobId: string) {
  const queryClient = useQueryClient();

  const { data: executions = [], isLoading } = useSseWithPollingFallback<JobExecution[]>({
    queryKey: ['job-executions', jobId],
    subscribeSSE: () => api.jobExecutionSSE(jobId, (data) => queryClient.setQueryData(['job-executions', jobId], data)),
    fetchFallbackFn: () => api.jobExecutions(jobId),
    refetchInterval: LONG_POLLING_INTERVAL,
  });

  return { executions, isLoading };
}

// Logs are fetched on mount and when the cache is explicitly invalidated.
export function useJobExecutionLogs(executionId: string) {
  return useQuery({
    queryKey: ['job-execution-logs', executionId],
    queryFn: async () => {
      const res = await api.logs(executionId);
      if (res === undefined) {
        showNotification({ type: 'error', message: 'Error while loading logs.' });
        return;
      }
      return res;
    },
    initialData: { logs: '' },
  });
}

export function useRefreshJobExecutionLogs(executionId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['job-execution-logs', executionId] });
  };
}

export function useJobConfig(job: JobListItem) {
  return useQuery({
    queryKey: ['job-config', job.id],
    queryFn: async () => {
      const res = await api.getJobConfig(job);
      if (res && !res.ok) {
        showNotification({ type: 'error', message: 'Error loading config.' });
      }
      return res;
    },
  });
}

export function useJobClusters(wbShortId: string) {
  return useQuery({
    queryKey: ['job-clusters', wbShortId],
    queryFn: () => api.clusters(wbShortId),
    initialData: [],
  });
}

export function useUpdateJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (model: EditJobModel) => api.updateJobConfig(model),
    onSuccess: (res) => {
      if (!res?.ok) {
        showNotification({ type: 'error', message: res?.error ?? 'Error updating job' });
      }
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

export function useToggleJobEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (job: JobListItem) => api.updateJobEnabled(job, !job.enabled),
    onSuccess: (res, job) => {
      if (!res?.ok) {
        showNotification({ type: 'error', message: res?.error ?? 'Error updating job' });
        return;
      }
      queryClient.setQueryData<JobListItem[]>(['jobs'], (prev) =>
        prev?.map((j) => (j.id === job.id ? { ...j, enabled: !job.enabled } : j))
      );
    },
  });
}

export function useDeleteJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (job: JobListItem) => api.deleteJob(job),
    onSuccess: (res) => {
      showNotification(res?.ok ? { type: 'info', message: 'Removing job' } : { type: 'error', message: 'Error deleting job' });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

export function useTriggerJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, data }: { jobId: string; data: unknown }) => api.triggerJob(jobId, data),
    onSuccess: (res) => {
      if (!res?.ok) {
        showNotification({ type: 'error', message: 'Error triggering job' });
      }
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

export function useInterruptExecution() {
  return useMutation({
    mutationFn: (execution: JobExecution) => api.interruptExecution(execution.id),
    onSuccess: (res) => {
      if (!res?.ok) {
        showNotification({ type: 'error', message: 'Error while interrupting execution.' });
      }
    },
  });
}
